


// generateMapSnapshot.js

/**
 * Generates a map snapshot (1920x1080) using LINZ aerial tiles, a land polygon, and a plan overlay.
 * Requires: Tile URL template, basic URL parameters parsed, polygon, and plan data.
 */


import { fetchParcelBoundaryFromLINZ } from './fetchParcelBoundaryFromLINZ';


const TILE_SIZE = 256;
let CANVAS_WIDTH = 1920;  // default
let CANVAS_HEIGHT = 1080; // default
const TILE_TEMPLATE = 'https://basemaps.linz.govt.nz/v1/tiles/aerial/EPSG:3857/{z}/{x}/{y}.webp?api=d01ev807hkjx30gfs1cdfgt8qb8';


export async function generateMapSnapshot({
    centerLat, centerLng, zoom,
    landLat, landLng,
    overlayImageUrl, overlayCenterLat, overlayCenterLng, overlayRotationDeg = 0, mirrorOverlay = false,
    overlayX, overlayY,
    boundaryScale = 1
}) {

    // Adjust this depending on whether we want an SD or HD image.
    if (boundaryScale === 2) {
        CANVAS_WIDTH = 3840;
        CANVAS_HEIGHT = 2160;
    }
    else {
        CANVAS_WIDTH = 1920;
        CANVAS_HEIGHT = 1080;
    }

    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');

    const centerPx = project(centerLat, centerLng, zoom);

    const topLeftPx = {
        x: centerPx.x - CANVAS_WIDTH / 2,
        y: centerPx.y - CANVAS_HEIGHT / 2
    };

    // Draw the tiles.
    await drawTiles(ctx, topLeftPx, zoom);

    // Draw land parcel boundary
    await drawParcelBoundaryOnCanvas(ctx, centerLat, centerLng, zoom, landLat, landLng, boundaryScale);

    // And now the overlay image.
    if (overlayImageUrl) {
        await drawOverlay(ctx, overlayImageUrl, centerLat, centerLng, overlayCenterLat, overlayCenterLng, overlayRotationDeg, mirrorOverlay, zoom, overlayX, overlayY);
    }

    const dataURL = canvas.toDataURL('image/png');
    downloadImage(dataURL, 'map-snapshot.png');
}


function project(lat, lng, zoom) {  // Web Mercator projection
    const sinLat = Math.sin(lat * Math.PI / 180);
    const x = (lng + 180) / 360 * Math.pow(2, zoom) * TILE_SIZE;
    const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * Math.pow(2, zoom) * TILE_SIZE;
    return { x, y };
}


// Tiles
async function drawTiles(ctx, topLeftPx, zoom) {
    const startTileX = Math.floor(topLeftPx.x / TILE_SIZE);
    const startTileY = Math.floor(topLeftPx.y / TILE_SIZE);

    const endTileX = Math.floor((topLeftPx.x + CANVAS_WIDTH) / TILE_SIZE);
    const endTileY = Math.floor((topLeftPx.y + CANVAS_HEIGHT) / TILE_SIZE);

    for (let tileY = startTileY; tileY <= endTileY; tileY++) {

        for (let tileX = startTileX; tileX <= endTileX; tileX++) {

            const url = TILE_TEMPLATE
                .replace('{z}', zoom)
                .replace('{x}', tileX)
                .replace('{y}', tileY);

            try {
                const img = await loadImage(url);

                const dx = (tileX * TILE_SIZE) - topLeftPx.x;
                const dy = (tileY * TILE_SIZE) - topLeftPx.y;

                ctx.drawImage(img, dx, dy, TILE_SIZE, TILE_SIZE);

            }
            catch (e) {
                console.warn('Failed to load tile:', url, e);
                ctx.fillStyle = '#cccccc'; // grey background if missing
                const dx = (tileX * TILE_SIZE) - topLeftPx.x;
                const dy = (tileY * TILE_SIZE) - topLeftPx.y;
                ctx.fillRect(dx, dy, TILE_SIZE, TILE_SIZE);
            }
        }
    }
}


// Parcel boundary.
async function drawParcelBoundaryOnCanvas(ctx, centerLat, centerLng, zoom, landLat, landLng, boundaryScale) {

    const canvasCenter = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
    const mapCenterProjected = project(centerLat, centerLng, zoom);

    let parcelGeometry = null;

    // If we have landLat/lng, query LINZ live
    if (landLat && landLng) {

        try {
            const parcelResult = await fetchParcelBoundaryFromLINZ(landLat, landLng);
            if (parcelResult && parcelResult.geometry) {
                parcelGeometry = parcelResult.geometry;
            }
        }
        catch (error) {
            console.error('Error fetching LINZ parcel for snapshot:', error);
        }
    }

    if (!parcelGeometry) {
        console.warn('No parcel boundary available to draw.');
        return;
    }

    if (parcelGeometry.type === 'Polygon') {
        //console.log('Drawing Polygon boundaries on snapshot:', parcelGeometry.coordinates);
        drawGeometry(ctx, parcelGeometry.coordinates, canvasCenter, mapCenterProjected, zoom, boundaryScale);
    }
    else if (parcelGeometry.type === 'MultiPolygon') {
        //console.log('Drawing MultiPolygon boundaries on snapshot:', parcelGeometry.coordinates);
        parcelGeometry.coordinates.forEach(polygon => {
            drawGeometry(ctx, polygon, canvasCenter, mapCenterProjected, zoom, boundaryScale);
        });
    }
}


function drawGeometry(ctx, coordinates, canvasCenter, mapCenterProjected, zoom, boundaryScale) {

    const flatCoords = coordinates[0];

    const path = flatCoords.map(([lng, lat]) => {
        const projected = project(lat, lng, zoom);
        const dx = projected.x - mapCenterProjected.x;
        const dy = projected.y - mapCenterProjected.y;
        return {
            x: canvasCenter.x + dx,
            y: canvasCenter.y + dy
        };
    });

    const phBlue = getComputedStyle(document.documentElement)
        .getPropertyValue('--phblue')
        .trim() || '#596A99'; // fallback if CSS not loaded

    ctx.beginPath();
    path.forEach((point, index) => {
        if (index === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
    });
    ctx.closePath();

    ctx.strokeStyle = phBlue;
    ctx.lineWidth = 4 * boundaryScale;

    ctx.stroke();


    // Now add the boundary node markers.
    path.forEach((point) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 5 * boundaryScale, 0, 2 * Math.PI);
        ctx.fillStyle = phBlue;
        ctx.fill();
    });


    // And calcualate the boundary lengths.
    for (let i = 0; i < path.length - 1; i++) {

        const p1 = flatCoords[i];
        const p2 = flatCoords[i + 1];

        const lat1 = p1[1];
        const lng1 = p1[0];
        const lat2 = p2[1];
        const lng2 = p2[0];

        const distanceMeters = computeDistanceBetween(lat1, lng1, lat2, lng2);

        if (distanceMeters < 1.0) continue;  // Skip small segments.

        const formatted = distanceMeters.toFixed(2);  // Always 2 dp, no "m" suffix

        // Find pixel midpoint for label
        const px1 = project(lat1, lng1, zoom);
        const px2 = project(lat2, lng2, zoom);

        const dx = px2.x - px1.x;
        const dy = px2.y - px1.y;

        const midX = canvasCenter.x + (px1.x - mapCenterProjected.x) + dx / 2;
        const midY = canvasCenter.y + (px1.y - mapCenterProjected.y) + dy / 2;

        drawPillLabel(ctx, midX, midY, formatted, boundaryScale);
    }
}


function computeDistanceBetween(lat1, lng1, lat2, lng2) {

    const toRad = (deg) => deg * Math.PI / 180;
    const a = 6378137.0; // semi-major axis

    const phi1 = toRad(lat1);
    const phi2 = toRad(lat2);
    const deltaPhi = toRad(lat2 - lat1);
    const deltaLambda = toRad(lng2 - lng1);

    const sinDeltaPhi = Math.sin(deltaPhi / 2);
    const sinDeltaLambda = Math.sin(deltaLambda / 2);

    const aVal = sinDeltaPhi * sinDeltaPhi +
        Math.cos(phi1) * Math.cos(phi2) *
        sinDeltaLambda * sinDeltaLambda;

    const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));

    const distance = a * c;
    return distance;
}


function drawPillLabel(ctx, x, y, text, boundaryScale) {

    const pillHeight = 18 * boundaryScale;
    const pillPadding = 10 * boundaryScale;
    const fontSize = 11 * boundaryScale;
    ctx.font = `${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif`;

    const textWidth = ctx.measureText(text).width;
    const pillWidth = textWidth + pillPadding * 2;
    const pillRadius = pillHeight / 2;

    ctx.beginPath();
    ctx.moveTo(x - pillWidth / 2 + pillRadius, y - pillHeight / 2);
    ctx.lineTo(x + pillWidth / 2 - pillRadius, y - pillHeight / 2);
    ctx.quadraticCurveTo(x + pillWidth / 2, y - pillHeight / 2, x + pillWidth / 2, y);
    ctx.quadraticCurveTo(x + pillWidth / 2, y + pillHeight / 2, x + pillWidth / 2 - pillRadius, y + pillHeight / 2);
    ctx.lineTo(x - pillWidth / 2 + pillRadius, y + pillHeight / 2);
    ctx.quadraticCurveTo(x - pillWidth / 2, y + pillHeight / 2, x - pillWidth / 2, y);
    ctx.quadraticCurveTo(x - pillWidth / 2, y - pillHeight / 2, x - pillWidth / 2 + pillRadius, y - pillHeight / 2);
    ctx.closePath();

    const phBlue = getComputedStyle(document.documentElement)
        .getPropertyValue('--phblue')
        .trim() || '#596A99';

    ctx.fillStyle = phBlue;
    ctx.fill();

    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y + 1);
}


// Plan overlay.
async function drawOverlay(ctx, imageUrl, mapCenterLat, mapCenterLng, overlayCenterLat, overlayCenterLng, rotationDeg, mirror, zoom, overlayX = 10, overlayY = 10) {

    const img = await loadImage(imageUrl);

    const canvasCenter = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };

    const mapCenterProjected = project(mapCenterLat, mapCenterLng, zoom);
    const overlayCenterProjected = project(overlayCenterLat, overlayCenterLng, zoom);

    const dx = overlayCenterProjected.x - mapCenterProjected.x;
    const dy = overlayCenterProjected.y - mapCenterProjected.y;

    const overlayCenterOnCanvas = {
        x: canvasCenter.x + dx,
        y: canvasCenter.y + dy,
    };

    // 🔥 Correct overlay bounds calculation
    const overlayHalfHeightDegrees = (overlayY / 2) / 111000;
    const overlayHalfWidthDegrees = (overlayX / 2) / (111000 * Math.cos(overlayCenterLat * Math.PI / 180));

    const north = overlayCenterLat + overlayHalfHeightDegrees;
    const south = overlayCenterLat - overlayHalfHeightDegrees;
    const east = overlayCenterLng + overlayHalfWidthDegrees;
    const west = overlayCenterLng - overlayHalfWidthDegrees;

    const topLeft = project(north, west, zoom);
    const bottomRight = project(south, east, zoom);

    const overlayWidthPixels = bottomRight.x - topLeft.x;
    const overlayHeightPixels = bottomRight.y - topLeft.y;

    // (Optional debug dots)
    /*
    ctx.beginPath();
    ctx.arc(canvasCenter.x, canvasCenter.y, 5, 0, 2 * Math.PI);
    ctx.fillStyle = 'red';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(overlayCenterOnCanvas.x, overlayCenterOnCanvas.y, 5, 0, 2 * Math.PI);
    ctx.fillStyle = 'yellow';
    ctx.fill();
    */

    ctx.save();
    ctx.translate(overlayCenterOnCanvas.x, overlayCenterOnCanvas.y);
    ctx.rotate((rotationDeg * Math.PI) / 180);
    if (mirror) {
        ctx.scale(-1, 1);
    }
    ctx.drawImage(img, -overlayWidthPixels / 2, -overlayHeightPixels / 2, overlayWidthPixels, overlayHeightPixels);
    ctx.restore();
}


// Final image handling.
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (e) => {
            console.error('Failed to load image:', img.src);
            reject(new Error(`Failed to load image: ${img.src}`));
        };

        // Add allowCrossOrigin=true if loading from Parse file server
        if (url.includes('parsefiles.back4app.com') && !url.includes('allowCrossOrigin=true')) {
            if (url.includes('?')) {
                img.src = url + '&allowCrossOrigin=true';
            } else {
                img.src = url + '?allowCrossOrigin=true';
            }
        } else {
            img.src = url;
        }
    });
}


function downloadImage(dataUrl, filename) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}



